import { createBrowserRouter } from 'react-router';
import type { RouteObject } from 'react-router';

import App from './app';
import {
  ANNOTATION_SETUP_ROUTE,
  ANNOTATION_EDITOR_ROUTE,
  loadAnnotationSetupPage,
  loadAnnotationEditorPage,
} from './components/Datalake/DatasetAnnotationsTab/AnnotationEditor/index.js';
import {
  JUDGE_FACTORY_ROUTE_PATTERN,
  loadJudgeFactoryPage,
} from './features/judge-factory';
import { AgentDebugPage } from './pages/AgentDebugPage/AgentDebugPage';
import { AgentForm } from './pages/AgentsPage/AgentForm/AgentForm';
import { AgentsPage } from './pages/AgentsPage/AgentsPage';
import { CallerPage } from './pages/CallerPage/CallerPage';
import {
  DatasetDetailsPage,
  DatasetsPage,
} from './pages/DatasetsPage/DatasetsPage';
import { EvalReportPage } from './pages/evalreportpage/EvalReportPage';
import { EvalRunsPage } from './pages/evalrunspage/EvalRunsPage';
import { EvaluationTemplateForm } from './pages/EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm';
import { EvaluationTemplatesPage } from './pages/EvaluationTemplatesPage/EvaluationTemplatesPage';
import { EvaluationTenantsPage } from './pages/EvaluationTenantsPage/EvaluationTenantsPage';
import { GraderPage } from './pages/GraderPage/GraderPage';
import { HomePage } from './pages/HomePage/HomePage';
import { NewSyntheticDatasetPage } from './pages/NewSyntheticDatasetPage/NewSyntheticDatasetPage';
import { NotFoundPage } from './pages/NotFoundPage/NotFoundPage';
import { PermissionsPage } from './pages/PermissionsPage/PermissionsPage';
import { SharedSignalsPage } from './pages/SharedSignalsPage/SharedSignalsPage';
import { SigsTestPage } from './pages/SigsTestPage/SigsTestPage';
import { SyntheticJobPreviewPage } from './pages/SyntheticJobPreviewPage/SyntheticJobPreviewPage';
import { TestSetsPage } from './pages/TestSetsPage/TestSetsPage';
import { TrendsPage } from './pages/TrendsPage/TrendsPage';
import {
  dynamicTelemetryHandle,
  telemetryHandle,
} from './telemetry/routeTelemetry.js';
import { PanelType, ViewType } from './telemetry/userbi.js';

const DATASET_TELEMETRY_TABS = new Set([
  'data-items',
  'overview',
  'versions',
  'attributes',
  'annotations',
  'permissions',
]);

export const EVALUATION_TENANTS_TELEMETRY = telemetryHandle(
  PanelType.EvaluationTenantsPage,
  ViewType.Overview,
);

export const routes: RouteObject[] = [
  {
    Component: App,
    children: [
      {
        index: true,
        Component: HomePage,
        handle: telemetryHandle(PanelType.HomePage, ViewType.Overview),
      },
      {
        path: 'evaluation-tenants',
        Component: EvaluationTenantsPage,
        handle: EVALUATION_TENANTS_TELEMETRY,
      },
      {
        path: 'sigs-test',
        Component: SigsTestPage,
        handle: telemetryHandle(PanelType.SigsTestPage, ViewType.Tool),
      },
      {
        path: 'debug',
        Component: AgentDebugPage,
        handle: telemetryHandle(PanelType.AgentDebugPage, ViewType.Tool),
      },
      {
        path: 'shared-signals',
        Component: SharedSignalsPage,
        handle: telemetryHandle(PanelType.SharedSignalsPage, ViewType.List),
      },
      {
        path: 'caller',
        Component: CallerPage,
        handle: telemetryHandle(PanelType.CallerPage, ViewType.Tool),
      },
      {
        path: 'datasets',
        Component: DatasetsPage,
        handle: dynamicTelemetryHandle(({ location }) => {
          const searchParams = new URLSearchParams(location.search);
          const tab = searchParams.get('tab');
          const isDetail = searchParams.has('datasetId');
          return {
            panelType: PanelType.DatasetsPage,
            view: isDetail ? ViewType.Detail : ViewType.List,
            data:
              isDetail && tab !== null && DATASET_TELEMETRY_TABS.has(tab)
                ? { 'Usage.Tab': tab }
                : undefined,
          };
        }),
      },
      {
        path: 'datasets/new-synthetic',
        Component: NewSyntheticDatasetPage,
        handle: telemetryHandle(PanelType.DatasetsPage, ViewType.Create),
      },
      {
        path: 'datasets/jobs/:jobId',
        Component: SyntheticJobPreviewPage,
        handle: telemetryHandle(PanelType.DatasetsPage, ViewType.Run),
      },
      {
        path: 'datasets/:datasetId',
        Component: DatasetDetailsPage,
        children: [
          {
            path: ANNOTATION_SETUP_ROUTE,
            lazy: loadAnnotationSetupPage,
            handle: telemetryHandle(
              PanelType.AnnotationEditorPage,
              ViewType.Create,
            ),
          },
          {
            path: ANNOTATION_EDITOR_ROUTE,
            lazy: loadAnnotationEditorPage,
            handle: telemetryHandle(
              PanelType.AnnotationEditorPage,
              ViewType.Edit,
            ),
          },
        ],
        handle: dynamicTelemetryHandle(({ location }) => {
          const tab = new URLSearchParams(location.search).get('tab');
          return {
            panelType: PanelType.DatasetsPage,
            view: ViewType.Detail,
            data:
              tab !== null && DATASET_TELEMETRY_TABS.has(tab)
                ? { 'Usage.Tab': tab }
                : undefined,
          };
        }),
      },
      {
        path: 'testsets',
        Component: TestSetsPage,
        handle: dynamicTelemetryHandle(({ location }) => ({
          panelType: PanelType.TestSetsPage,
          view: new URLSearchParams(location.search).has('testSetId')
            ? ViewType.Detail
            : ViewType.List,
        })),
      },
      {
        path: 'workspaces/:workspaceName/eval-runs',
        Component: EvalRunsPage,
        handle: telemetryHandle(PanelType.EvaluationRunsPage, ViewType.List),
      },
      {
        path: 'workspaces/:workspaceName/trends',
        Component: TrendsPage,
        handle: telemetryHandle(PanelType.TrendsPage, ViewType.Overview),
      },
      {
        path: 'workspaces/:workspaceName/agents',
        Component: AgentsPage,
        handle: telemetryHandle(PanelType.AgentsPage, ViewType.List),
        children: [
          {
            path: 'new',
            Component: AgentForm,
            handle: telemetryHandle(PanelType.AgentsPage, ViewType.Create),
          },
          {
            path: ':agentName',
            handle: telemetryHandle(PanelType.AgentsPage, ViewType.Detail),
          },
        ],
      },
      {
        path: 'workspaces/:workspaceName/graders',
        Component: GraderPage,
        handle: telemetryHandle(PanelType.GradersPage, ViewType.List),
        children: [
          {
            path: ':judgeId',
            handle: telemetryHandle(PanelType.GradersPage, ViewType.Detail),
          },
        ],
      },
      {
        path: JUDGE_FACTORY_ROUTE_PATTERN,
        handle: dynamicTelemetryHandle(({ params }) => ({
          panelType: PanelType.JudgeFactoryPage,
          view: params.judgeId === undefined ? ViewType.List : ViewType.Detail,
        })),
        lazy: async () => {
          const { JudgeFactoryPage } = await loadJudgeFactoryPage();
          return { Component: JudgeFactoryPage };
        },
      },
      {
        path: 'workspaces/:workspaceName/evaluation-templates',
        Component: EvaluationTemplatesPage,
        handle: telemetryHandle(
          PanelType.EvaluationTemplatesPage,
          ViewType.List,
        ),
        children: [
          {
            path: 'new',
            Component: EvaluationTemplateForm,
            handle: telemetryHandle(
              PanelType.EvaluationTemplatesPage,
              ViewType.Create,
            ),
          },
          {
            path: ':templateName',
            handle: telemetryHandle(
              PanelType.EvaluationTemplatesPage,
              ViewType.Detail,
            ),
          },
        ],
      },
      {
        path: 'permissions',
        Component: PermissionsPage,
        handle: telemetryHandle(PanelType.PermissionsPage, ViewType.Overview),
      },
      {
        path: 'workspaces/:workspaceName/evaluation/evaluation-runs/:id/report',
        Component: EvalReportPage,
        handle: telemetryHandle(PanelType.EvaluationRunsPage, ViewType.Report),
      },
      { path: '*', Component: NotFoundPage },
    ],
  },
];

export const router = createBrowserRouter(routes);
